export const GENERAL_ENQUIRIES_INBOX_KEY = "general_enquiries";
// An Inbox that a retired Team's creation made keeps the key `team:<id>`.
// Only Release 1 `?team=` links and saved Team choices use it now
// (util/inbox-scope), to find that Inbox.
const TEAM_INBOX_KEY_PREFIX = "team:";

export const teamInboxKey = (teamId: string): string =>
  `${TEAM_INBOX_KEY_PREFIX}${teamId}`;

// Which conversations of an Inbox a grant gives (fuzey-api ConversationScope).
// Every Inbox is open in this iteration, so a grant only adds to what the
// role gives: `own` adds nothing. It never means "only mine".
export type ConversationScope = "own" | "own_and_unassigned" | "all";

export const CONVERSATION_SCOPES: ConversationScope[] = [
  "own",
  "own_and_unassigned",
  "all",
];

/**
 * Two capabilities inside one Inbox. A person's own conversations need
 * neither. "Colleagues' but not unassigned" exists only as a role result:
 * no grant scope gives it.
 */
export interface InboxAccessBits {
  others: boolean;
  unassigned: boolean;
}

export const NO_INBOX_ACCESS: InboxAccessBits = {
  others: false,
  unassigned: false,
};

/**
 * One person's access in one Inbox, as the server reports it. Capabilities
 * come from `effective` only: it follows the server's evaluation mode, so a
 * stored grant scope can sit there without being in effect yet.
 */
export interface InboxMyAccess {
  grantScope: ConversationScope | null;
  inherited: InboxAccessBits;
  effective: InboxAccessBits;
}

/**
 * Where a conversation belongs: General Enquiries and the Inboxes made in
 * Settings → Inboxes (see managed-inbox-domain).
 */
export default class InboxDomain {
  id: string;

  key: string;

  name: string;

  isActive: boolean;

  // The signed-in person's access here. Null when the server did not send
  // it: callers then fall back to what the role gives, never more.
  myAccess: InboxMyAccess | null;

  constructor(
    id: string,
    key: string,
    name: string,
    isActive: boolean,
    myAccess: InboxMyAccess | null = null
  ) {
    this.id = id;
    this.key = key;
    this.name = name;
    this.isActive = isActive;
    this.myAccess = myAccess;
  }

  isGeneralEnquiries(): boolean {
    return this.key === GENERAL_ENQUIRIES_INBOX_KEY;
  }
}
